import { useState, useCallback, useRef, useEffect } from "react";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import axios from "axios";

import Dashboard from "@/components/Dashboard";
import UploadPanel from "@/components/UploadPanel";
import VolumeViewer from "@/components/VolumeViewer";
import TimeLapsePlayer from "@/components/TimeLapsePlayer";
import MetricsPanel from "@/components/MetricsPanel";
import ChartsPanel from "@/components/ChartsPanel";
import Header from "@/components/Header";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VisitorStats = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/stats`);
        setStats(response.data);
      } catch (e) {
        console.error("Failed to fetch stats", e);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (loading) return <div className="p-8 text-center">Loading stats...</div>;
  if (!stats) return <div className="p-8 text-center">Stats not available</div>;

  return (
    <div className="max-w-4xl mx-auto p-8">
      <h1 className="text-2xl font-bold mb-6">Visitor Statistics</h1>
      <div className="bg-white p-6 rounded-lg shadow mb-8">
        <p className="text-4xl font-bold text-blue-600">{stats.total_visits.toLocaleString()}</p>
        <p className="text-gray-500">Total Visits</p>
      </div>

      <h2 className="text-xl font-semibold mb-4">Recent Log</h2>
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">IP</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Path</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User Agent</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {stats.recent_visitors.map((v, i) => (
              <tr key={i}>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {new Date(v.timestamp).toLocaleString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">{v.ip}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{v.path}</td>
                <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate" title={v.user_agent}>{v.user_agent}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-8 text-center">
        <button
          onClick={() => window.location.href = '/'}
          className="bg-gray-200 hover:bg-gray-300 px-4 py-2 rounded text-gray-800"
        >
          Back to App
        </button>
      </div>
    </div>
  );
};

function App() {
  const [analysisData, setAnalysisData] = useState(null);
  const [timeLapseData, setTimeLapseData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [activeView, setActiveView] = useState("3d"); // "3d" or "timelapse"
  const [thresholdMethod, setThresholdMethod] = useState("otsu");
  const chartsPanelRef = useRef(null);
  const [totalVisits, setTotalVisits] = useState(0);

  useEffect(() => {
    const logVisit = async () => {
      try {
        const response = await axios.post(`${API}/visit?path=${window.location.pathname}`);
        if (response.data?.success) {
          setTotalVisits(response.data.total_visits);
        }
      } catch (e) {
        console.error("Failed to log visit:", e);
      }
    };
    logVisit();
  }, []);

  // Lifted state for preserving files between analysis runs
  const [uploadState, setUploadState] = useState({
    redFile: null,
    greenFile: null,
    previewData: null,
    currentZ: 0,
    rois: [
      {
        id: "roi-1",
        name: "Center ROI",
        visible: true,
        xRange: [25, 75],
        yRange: [25, 75],
        zRange: [0, 100],
        color: "#3b82f6"
      }
    ],
    activeRoiId: "roi-1",
    roiEnabled: false,
    isTimeLapse: false
  });

  const handleAnalyze = useCallback(async (redFile, greenFile, options = {}) => {
    setIsLoading(true);

    const formData = new FormData();
    formData.append("red_channel", redFile);
    formData.append("green_channel", greenFile);
    formData.append("threshold_method", options.thresholdMethod || thresholdMethod);
    formData.append("percentile", options.percentile || 75);
    formData.append("pearson_mode", options.pearsonMode || "union_fg");
    formData.append("include_volumes", options.includeVolumes ?? true);
    formData.append("max_xy_size", options.maxXYSize || 256);
    formData.append("max_voxels", options.maxVoxels || 1500000);


    // ROI parameters
    formData.append("roi_enabled", options.roiEnabled || false);
    formData.append("roi_x_min", options.roiXMin ?? 0);
    formData.append("roi_x_max", options.roiXMax ?? 100);
    formData.append("roi_y_min", options.roiYMin ?? 0);
    formData.append("roi_y_max", options.roiYMax ?? 100);
    formData.append("roi_z_min", options.roiZMin ?? 0);
    formData.append("roi_z_max", options.roiZMax ?? 100);

    try {
      const response = await axios.post(`${API}/upload/analyze`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      setAnalysisData(response.data);
      setActiveView("3d");

      const roiText = response.data.roi?.enabled
        ? ` (ROI: ${response.data.roi.roi_bounds.x[1] - response.data.roi.roi_bounds.x[0]}×${response.data.roi.roi_bounds.y[1] - response.data.roi.roi_bounds.y[0]}×${response.data.roi.roi_bounds.z[1] - response.data.roi.roi_bounds.z[0]})`
        : "";

      toast.success("Analysis complete" + roiText, {
        description: `Pearson r: ${response.data.metrics.pearson_r.toFixed(4)}`
      });
    } catch (error) {
      console.error("Analysis error:", error);
      toast.error("Analysis failed", {
        description: error.response?.data?.detail || error.message
      });
    } finally {
      setIsLoading(false);
    }
  }, [thresholdMethod]);

  const handleTimeLapseAnalyze = useCallback(async (redFile, greenFile, options = {}) => {
    setIsLoading(true);

    const formData = new FormData();
    formData.append("red_channel", redFile);
    formData.append("green_channel", greenFile);
    formData.append("threshold_method", options.thresholdMethod || thresholdMethod);
    formData.append("threshold_strategy", options.strategy || "per_frame");
    formData.append("pearson_mode", options.pearsonMode || "union_fg");
    formData.append("include_frames_data", String(options.includeFramesData ?? true));
    formData.append("max_frame_dim", options.maxFrameDim || 512);

    // ROI parameters (X and Y only for time-lapse)
    const roiEnabled = options.roiEnabled === true;
    formData.append("roi_enabled", String(roiEnabled));
    formData.append("roi_x_min", options.roiXMin ?? 0);
    formData.append("roi_x_max", options.roiXMax ?? 100);
    formData.append("roi_y_min", options.roiYMin ?? 0);
    formData.append("roi_y_max", options.roiYMax ?? 100);

    console.log("Time-lapse ROI:", { roiEnabled, xMin: options.roiXMin, xMax: options.roiXMax, yMin: options.roiYMin, yMax: options.roiYMax });

    try {
      const response = await axios.post(`${API}/upload/timelapse`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      setTimeLapseData(response.data);
      setActiveView("timelapse");

      const roiText = response.data.roi?.enabled
        ? ` (ROI: ${response.data.roi.roi_bounds.x[1] - response.data.roi.roi_bounds.x[0]}×${response.data.roi.roi_bounds.y[1] - response.data.roi.roi_bounds.y[0]})`
        : "";

      toast.success("Time-lapse analysis complete" + roiText, {
        description: `${response.data.n_frames} frames processed`
      });
    } catch (error) {
      console.error("Time-lapse error:", error);
      toast.error("Time-lapse analysis failed", {
        description: error.response?.data?.detail || error.message
      });
    } finally {
      setIsLoading(false);
    }
  }, [thresholdMethod]);

  const handleExport = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/analyses/export`);
      const data = response.data.data;

      if (!data || data.length === 0) {
        toast.info("No data to export");
        return;
      }

      // Format data for user-friendly CSV
      const formatValue = (value, key) => {
        if (value === null || value === undefined) return "";

        // Format timestamp to readable date/time
        if (key === "timestamp") {
          try {
            const date = new Date(value);
            return date.toLocaleString("en-US", {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit"
            });
          } catch {
            return value;
          }
        }

        // Round numeric values to 4 decimal places
        if (typeof value === "number") {
          if (Number.isInteger(value)) return value;
          return Math.round(value * 10000) / 10000;
        }

        return value;
      };

      // Define user-friendly column order and names
      const columnMapping = {
        "Analysis #": (row, index) => index + 1,
        "Date/Time": (row) => formatValue(row.timestamp, "timestamp"),
        "Threshold Method": (row) => row.threshold_method?.toUpperCase() || "",
        "Pearson r": (row) => formatValue(row.pearson_r, "number"),
        "Manders M1": (row) => formatValue(row.manders_m1, "number"),
        "Manders M2": (row) => formatValue(row.manders_m2, "number"),
        "Red Colocalized %": (row) => formatValue(row.fraction_coloc_red * 100, "number"),
        "Green Colocalized %": (row) => formatValue(row.fraction_coloc_green * 100, "number"),
        "Red Threshold": (row) => formatValue(row.threshold_red, "number"),
        "Green Threshold": (row) => formatValue(row.threshold_green, "number"),
        "Red FG Voxels": (row) => row.n_red_fg || "",
        "Green FG Voxels": (row) => row.n_green_fg || "",
        "Colocalized Voxels": (row) => row.n_coloc || ""
      };

      const headers = Object.keys(columnMapping);
      const csvRows = data.map((row, index) =>
        headers.map(header => {
          const value = columnMapping[header](row, index);
          // Escape values containing commas or quotes
          if (typeof value === "string" && (value.includes(",") || value.includes('"'))) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        }).join(",")
      );

      const csv = [headers.join(","), ...csvRows].join("\n");

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);

      // Try multiple download methods for browser compatibility
      const filename = `colocalization_analysis_${new Date().toISOString().slice(0, 10)}.csv`;

      // Method 1: Create and click anchor element
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();

      // Small delay before cleanup to ensure download starts
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 100);

      toast.success(`Exported: ${filename}`);
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Export failed: " + error.message);
    }
  }, []);

  // Debug log for dashboard access
  console.log("Current path:", window.location.pathname);
  const isDashboard = window.location.pathname.includes('/secret-dashboard');

  if (isDashboard && VisitorStats) {
    return <VisitorStats />;
  }

  return (
    <div className="min-h-screen bg-background" data-testid="app-container">
      <Toaster position="top-right" richColors />

      <Header
        activeView={activeView}
        setActiveView={setActiveView}
        onExport={handleExport}
        hasData={!!analysisData || !!timeLapseData}
        has3DData={!!analysisData}
        hasTimeLapseData={!!timeLapseData}
      />

      <main className="container mx-auto px-6 py-8">
        {!analysisData && !timeLapseData ? (
          <UploadPanel
            onAnalyze={handleAnalyze}
            onTimeLapseAnalyze={handleTimeLapseAnalyze}
            isLoading={isLoading}
            thresholdMethod={thresholdMethod}
            setThresholdMethod={setThresholdMethod}
            uploadState={uploadState}
            setUploadState={setUploadState}
          />
        ) : (
          <Dashboard
            activeView={activeView}
            analysisData={analysisData}
            timeLapseData={timeLapseData}
            chartsPanelRef={chartsPanelRef}
            onReset={() => {
              // Only clear analysis results, preserve upload state
              setAnalysisData(null);
              setTimeLapseData(null);
            }}
          >
            {activeView === "3d" && analysisData && (
              <>
                <div className="col-span-12 lg:col-span-8">
                  <VolumeViewer
                    redVolume={analysisData.volumes?.red}
                    greenVolume={analysisData.volumes?.green}
                  />
                </div>
                <div className="col-span-12 lg:col-span-4 space-y-6">
                  <MetricsPanel metrics={analysisData.metrics} />
                  <ChartsPanel
                    ref={chartsPanelRef}
                    histograms={analysisData.histograms}
                    scatter={analysisData.scatter}
                    ccf={analysisData.ccf}
                    thresholds={{
                      red: analysisData.metrics?.threshold_red,
                      green: analysisData.metrics?.threshold_green
                    }}
                  />
                </div>
              </>
            )}

            {activeView === "timelapse" && timeLapseData && (
              <>
                <div className="col-span-12">
                  <TimeLapsePlayer
                    framesData={timeLapseData.frames_data}
                    frameResults={timeLapseData.frame_results}
                    nFrames={timeLapseData.n_frames}
                  />
                </div>
              </>
            )}
          </Dashboard>
        )}
      </main>

      <footer className="border-t mt-12 bg-muted/50">
        <div className="container mx-auto px-6 py-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} Coloc3D-STLp - Advanced Microscopy Analysis
          </div>
          {totalVisits > 0 && (
            <div className="flex items-center gap-2 text-xs font-mono text-muted-foreground/60 bg-background/50 px-3 py-1.5 rounded-full border">
              <div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse"></div>
              <span>{totalVisits.toLocaleString()} visitors</span>
            </div>
          )}
        </div>
      </footer>
    </div>
  );
}

export default App;
